<template>
  <div class="rentalplan-item">
    <div class="infos" :class="`infos--${statusClass}`">
      <div class="bill-header">
        <div class="bill-order-number">
          <span class="bill-order-number__label">{{ $t('rentalplan.orderNumber') }}</span>
          <span class="bill-order-number__value text-ellipsis">{{ displayOrderId || '-' }}</span>
          <button type="button" class="copy-order" :aria-label="$t('rentalplan.copyOrderNumber')" @click.stop="copyOrderNumber">
            <span class="copy-icon"></span>
          </button>
        </div>
      </div>
      <div class="bill-divider"></div>
      <div class="bill-period-row">
        <span class="date-icon"></span>
        <span class="period text-ellipsis">
          Rental: {{ formatRentalDate(plan.rentalStartDate) }} - {{ formatRentalDate(plan.rentalEndDate) }}
        </span>
        <div class="status" :class="statusClass">{{ statusText }}</div>
      </div>
      <div class="product flex align-center" @click="handlerClick('planDetail')">
        <div class="product__img" v-lazy:background-image="plan.goodsPic"></div>
        <div class="product__body">
          <div class="product__name text-ellipsis">{{ plan.goodsName || $t('rentalplan.unknownProduct') }}</div>
          <div class="product__sku text-ellipsis" v-if="plan.skuInfo">{{ plan.skuInfo }}</div>
        </div>
        <van-icon name="arrow" />
      </div>
      <div class="bill-divider"></div>
      <div class="bill-summary">
        <div class="bill-amount">
          <span>{{ $t('rentalplan.dueAmount') }}</span>
          <strong><span class="money-icon"></span>{{ formatRupiah(plan.amount) }}</strong>
        </div>
        <div class="bill-summary-divider"></div>
        <div class="bill-due-date">
          <span :class="{ overdue: overdue }">{{ $t('rentalplan.dueDate') }}</span>
          <strong :class="{ overdue: overdue }"><span class="date-icon date-icon--value"></span>{{ formatRentalDate(plan.dueDate) }}</strong>
        </div>
      </div>
      <div class="bill-divider"></div>
      <div class="bill-actions">
        <button v-if="showPayButton" type="button" :disabled="!plan.canPay" @click.stop="handlerClick('payNow')">
          {{ $t('rentalplan.payNow') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from 'vant';
import { REPAY_LIST_STATUS } from '@/common/constants';
import { formatRentalDate } from '@/filters';
import { copyText } from '@/utils/clipboard';
import { formatRupiahAmount } from '@/utils/money';
export default {
  name: 'RentalplanItem',
  components: { 'van-icon': Icon },
  props: {
    plan: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
    }
  },
  computed: {
    overdue() {
      return [REPAY_LIST_STATUS.OVERDUE].includes(String(this.plan.status || '').toUpperCase());
    },
    statusClass() {
      if (this.overdue) return 'overdue';
      const status = String(this.plan.status || '').toUpperCase();
      if (['SETTLED', 'PAID', 'SUCCESS'].includes(status)) return 'paid';
      if (['UPCOMING', 'FROZEN', 'CANCELLED'].includes(String(this.plan.status || '').toUpperCase())) return 'upcoming';
      return 'unpaid';
    },
    statusText() {
      if (this.overdue) return this.$t('rentalplan.overdue');
      const status = String(this.plan.status || '').toUpperCase();
      if (['SETTLED', 'PAID', 'SUCCESS'].includes(status)) return this.$t('rentalplan.paid');
      if (this.statusClass === 'upcoming') return this.$t('rentalplan.upcoming');
      return this.$t('rentalplan.unpaid');
    },
    displayOrderId() {
      return String(this.plan.rentalOrderId || '').trim();
    },
    showPayButton() {
      return this.plan.canPay && ['unpaid', 'overdue'].includes(this.statusClass);
    }
  },
  methods: {
    async copyOrderNumber() {
      if (!this.displayOrderId) return;
      const success = await copyText(this.displayOrderId);
      this.$toast(this.$t(success ? 'rentalplan.copySuccess' : 'rentalplan.copyFailed'));
    },
    handlerClick(type) {
      this.$emit('planClick', {
        type: type,
        item: this.plan
      })
    },
    formatRupiah(amount) {
      return `Rp${formatRupiahAmount(amount) || '0'}`;
    },
    formatRentalDate(value) {
      return formatRentalDate(value, '--');
    },
    parseRentalDate(value) {
      if (value === null || value === undefined || String(value).trim() === '' || String(value).trim() === '0') return null;
      if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        const numeric = Number(value);
        const timestamp = numeric < 1000000000000 ? numeric * 1000 : numeric;
        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? null : date;
      }
      const normalized = String(value).trim().replace(/-/g, '/');
      const date = new Date(normalized);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }
}
</script>

<style lang="less" scoped>
.rentalplan-item {
  margin-top: 12px;

  &:first-child { margin-top: 0; }
}

.infos {
  position: relative;
  padding: 16px;
  overflow: hidden;
  border: 1px solid #E5EAF3;
  border-radius: 10px;
  background: #FFFFFF;
}

.infos::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  background: #B45309;
  content: '';
}

.infos--paid::before { background: #228B55; }
.infos--overdue::before { background: #D83A00; }
.infos--upcoming::before { background: #64748B; }

.bill-header,
.bill-order-number,
.bill-period-row,
.bill-summary,
.bill-amount,
.bill-due-date {
  display: flex;
  align-items: center;
}

.bill-order-number {
  min-width: 0;
  max-width: 100%;
  color: #070D3F;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
}

.bill-order-number__label {
  flex: 0 0 auto;
  margin-right: 4px;
}

.bill-order-number__value {
  flex: 0 1 auto;
  min-width: 0;
}

.copy-order {
  flex: 0 0 auto;
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  width: 12px;
  height: 14px;
}

.bill-divider {
  height: 1px;
  margin: 12px 0;
  background: #E5EAF3;
}

.bill-period-row {
  min-width: 0;
  gap: 6px;
  color: #64748B;
  font-size: 12px;
  line-height: 16px;
}

.date-icon,
.money-icon,
.copy-icon {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  box-sizing: border-box;
}

.date-icon {
  width: 16px;
  height: 16px;
  border: 1px solid currentColor;
  border-radius: 3px;
  color: #64748B;
}

.date-icon::before {
  position: absolute;
  top: 3px;
  left: 2px;
  right: 2px;
  height: 1px;
  background: currentColor;
  content: '';
}

.date-icon::after {
  position: absolute;
  top: -3px;
  left: 4px;
  width: 6px;
  height: 5px;
  border-left: 1px solid currentColor;
  border-right: 1px solid currentColor;
  content: '';
}

.date-icon--value { color: currentColor; }

.money-icon {
  width: 18px;
  height: 12px;
  border: 2px solid #0B45FF;
  border-radius: 2px;
  background: #0B45FF;
}

.money-icon::after {
  position: absolute;
  top: 2px;
  left: 6px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #FFFFFF;
  content: '';
}

.copy-icon {
  width: 10px;
  height: 13px;
  border: 2px solid #0B45FF;
  border-radius: 2px;
}

.copy-icon::before {
  position: absolute;
  top: -4px;
  left: 3px;
  width: 10px;
  height: 13px;
  border: 2px solid #0B45FF;
  border-radius: 2px;
  background: #FFFFFF;
  content: '';
}

.period { flex: 1; min-width: 0; }

.status {
  flex: 0 0 auto;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
}

.status.paid { color: #228B55; background: #EAF8F0; }
.status.unpaid { color: #B45309; background: #FFF4DF; }
.status.overdue { color: #D83A00; background: #FFF0EB; }
.status.upcoming { color: #64748B; background: #F3F4F6; }

.product {
  min-width: 0;
  gap: 12px;
  cursor: pointer;
}

.product__img {
  flex: 0 0 72px;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  background: #F6F8FC center/contain no-repeat;
}

.product__body { min-width: 0; flex: 1; }
.product__name { color: #070D3F; font-size: 13px; line-height: 18px; font-weight: 500; }
.product__sku { margin-top: 4px; color: #64748B; font-size: 12px; line-height: 16px; }
.product > .van-icon { flex: 0 0 auto; color: #64748B; font-size: 20px; }

.bill-summary {
  min-width: 0;
  gap: 12px;
}

.bill-amount,
.bill-due-date {
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.bill-amount > span,
.bill-due-date > span {
  color: #64748B;
  font-size: 12px;
  line-height: 16px;
}

.bill-amount > strong,
.bill-due-date > strong {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 5px;
  color: #0B45FF;
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
}

.bill-amount > strong > .van-icon,
.bill-due-date > strong > .van-icon { flex: 0 0 auto; font-size: 16px; }
.bill-due-date { text-align: center; }
.bill-due-date > strong { color: #070D3F; font-size: 12px; }
.bill-due-date > strong.overdue { color: #D83A00; }
.bill-due-date > span.overdue { color: #D83A00; }

.bill-summary-divider {
  flex: 0 0 1px;
  height: 44px;
  background: #E5EAF3;
}

.bill-actions {
  display: flex;
  justify-content: flex-end;
  min-height: 0;
}

.bill-actions button {
  min-width: 96px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #0B45FF;
  border-radius: 8px;
  background: #0B45FF;
  color: #FFFFFF;
  font-size: 12px;
  line-height: 38px;
}

.bill-actions button:disabled {
  opacity: 0.45;
}
</style>

<style lang="less" scoped>
.rentalplan-item { margin-top: 24px; }
.infos { padding: 32px; border-width: 2px; border-radius: 20px; }
.infos::before { width: 6px; }
.bill-order-number { font-size: 26px; line-height: 36px; }
.bill-order-number__label { margin-right: 8px; }
.copy-order { margin-left: 8px; width: 24px; height: 28px; }
.bill-divider { height: 2px; margin: 24px 0; }
.bill-period-row { gap: 12px; font-size: 24px; line-height: 32px; }
.status { padding: 8px 16px; font-size: 22px; line-height: 32px; }
.product { gap: 24px; }
.product__img { flex-basis: 144px; width: 144px; height: 144px; border-radius: 16px; }
.product__name { font-size: 26px; line-height: 36px; }
.product__sku { margin-top: 8px; font-size: 24px; line-height: 32px; }
.product > .van-icon { font-size: 36px; }
.bill-summary { gap: 24px; }
.bill-amount,
.bill-due-date { gap: 8px; }
.bill-amount > span,
.bill-due-date > span { font-size: 24px; line-height: 32px; }
.bill-amount > strong,
.bill-due-date > strong { gap: 10px; font-size: 30px; line-height: 40px; }
.bill-due-date > strong { font-size: 24px; }
.bill-summary-divider { flex-basis: 2px; height: 88px; }
.bill-actions { min-height: 0; }
.bill-actions button { min-width: 192px; height: 80px; padding: 0 28px; border-width: 2px; border-radius: 16px; font-size: 24px; line-height: 76px; }
.date-icon { width: 32px; height: 32px; border-width: 2px; border-radius: 6px; }
.date-icon::before { top: 6px; left: 4px; right: 4px; height: 2px; }
.date-icon::after { top: -6px; left: 8px; width: 12px; height: 10px; border-left-width: 2px; border-right-width: 2px; }
.money-icon { width: 36px; height: 24px; border-width: 4px; border-radius: 4px; }
.money-icon::after { top: 4px; left: 12px; width: 8px; height: 8px; }
.copy-icon { width: 20px; height: 26px; border-width: 4px; border-radius: 4px; }
.copy-icon::before { top: -8px; left: 6px; width: 20px; height: 26px; border-width: 4px; border-radius: 4px; }
</style>
